<template>
  <section class="mx-auto max-w-[1440px] space-y-4 px-3 py-4 sm:px-5 lg:px-6">
    <div v-if="loading" class="rounded-[24px] border border-dashed border-[#eadfd4] bg-white p-5 text-sm text-muted">
      Loading venue details...
    </div>

    <div v-else class="space-y-4">
      <div v-if="error" class="rounded-[20px] border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-800">
        {{ error }}
      </div>

      <section class="overflow-hidden rounded-[26px] border border-[#eadfd4] bg-white shadow-sm">
        <div class="border-b border-[#efe3d8] px-4 py-3 sm:px-5 lg:px-6">
          <nav class="mb-3 flex flex-wrap items-center gap-2 text-xs text-muted sm:text-sm">
            <i class="ri-store-2-line text-base text-brand"></i>
            <span class="font-medium text-[#b78969]">Business</span>
            <span class="text-[#d2b9a3]">/</span>
            <span class="font-medium text-[#b78969]">{{ breadcrumbCategory }}</span>
            <span class="text-[#d2b9a3]">/</span>
            <span class="min-w-0 truncate font-semibold text-ink">{{ venue.title || 'Venue' }}</span>
          </nav>
        </div>

        <div class="space-y-3 px-4 py-4 sm:px-5 lg:px-6">
          <div v-if="heroGallery.length" class="mx-auto grid max-w-[1180px] grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4 lg:grid-rows-2">
            <button
              v-if="heroGallery[0]"
              type="button"
              class="relative aspect-[4/3] min-w-0 overflow-hidden rounded-[16px] text-left sm:rounded-[20px] lg:col-span-2 lg:row-span-2 lg:h-[420px] lg:min-h-0 lg:rounded-[24px]"
              @click="openInviteGalleryById(heroGallery[0])"
            >
              <img
                v-if="heroGallery[0]"
                :src="heroGallery[0].src"
                :alt="venue.title"
                class="block h-full w-full rounded-[16px] bg-[#f5efe8] object-cover sm:rounded-[20px] lg:rounded-[24px]"
                :class="{ 'object-contain p-4': heroGallery[0].isPlaceholder }"
              >
              <span v-if="heroGallery[0]?.isCover && !heroGallery[0]?.isPlaceholder" class="absolute left-4 top-4 rounded-full bg-white/95 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-brand shadow-sm">
                Cover image
              </span>
            </button>

            <button
              v-for="(image, index) in heroGallery.slice(1, 4)"
              :key="`${image.id || 'hero-side'}-${image.src || index}-${index}`"
              type="button"
              class="relative aspect-[4/3] min-w-0 overflow-hidden rounded-[16px] text-left sm:rounded-[20px] lg:h-[204px] lg:min-h-0"
              @click="openInviteGalleryById(image)"
            >
              <img
                :src="image.src"
                :alt="`${venue.title} ${index + 2}`"
                class="block h-full w-full rounded-[16px] bg-[#f5efe8] object-cover sm:rounded-[20px]"
                :class="{ 'object-contain p-3': image.isPlaceholder }"
              >
              <div
                v-if="image.overlayText"
                class="absolute inset-0 flex flex-col items-center justify-center bg-[#1f1208]/55 text-center text-white"
              >
                <span class="text-[1.45rem] font-semibold leading-none">{{ image.overlayText }}</span>
                <span class="mt-1.5 text-[10px] font-semibold uppercase tracking-[0.18em]">more photos</span>
              </div>
            </button>
          </div>

          <div class="border-b border-[#efe3d8] pb-3 pt-2">
            <div class="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
              <div class="min-w-0">
                <h1 class="flex flex-wrap items-center gap-2 text-[1.2rem] font-medium leading-tight text-[#4b5563] sm:text-[1.35rem] lg:text-[1.55rem]">
                  <span>{{ venue.title || 'Venue' }}</span>
                  <i class="ri-checkbox-circle-fill text-[1.25rem] text-[#1683ff]"></i>
                </h1>
                <p class="mt-3 flex items-start gap-1.5 text-[0.95rem] leading-5 text-[#4b5563]">
                  <i class="ri-map-pin-fill mt-0.5 text-[#4b5563]"></i>
                  <span>{{ venue.address || 'Location unavailable' }}</span>
                </p>
              </div>
              <div class="shrink-0 text-left md:text-right">
                <p class="text-[0.95rem] font-medium text-[#334155]">Talk to Venue Manager</p>
                <p class="mt-1 text-[1rem] font-semibold text-[#334155]">
                  <i class="ri-phone-fill align-[-1px]"></i>
                  {{ venueManagerPhone }}
                </p>
              </div>
            </div>

            <div v-if="venueHighlightsList.length || inHouseServicesList.length" class="mt-4 grid gap-4 lg:grid-cols-2">
              <div v-if="venueHighlightsList.length">
                <p class="text-xs font-semibold uppercase tracking-[0.14em] text-[#b78969]">Highlights</p>
                <div class="mt-2 flex flex-wrap gap-2">
                  <span v-for="item in venueHighlightsList" :key="item" class="rounded-full bg-[#f5eee8] px-3 py-1.5 text-sm font-semibold text-[#6d5542]">
                    {{ item }}
                  </span>
                </div>
              </div>

              <div v-if="inHouseServicesList.length">
                <p class="text-xs font-semibold uppercase tracking-[0.14em] text-[#b78969]">In-house services</p>
                <div class="mt-2 flex flex-wrap gap-2">
                  <span v-for="item in inHouseServicesList" :key="item" class="rounded-full bg-[#f5eee8] px-3 py-1.5 text-sm font-semibold text-[#6d5542]">
                    {{ item }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section class="grid min-w-0 gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
        <div class="min-w-0 space-y-4">
          <section v-if="spaces?.length" class="rounded-[24px] border border-[#eadfd4] bg-white p-4 shadow-sm sm:p-5">
            <div>
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Spaces</p>
              <h2 class="mt-2 font-serif text-[1.9rem] font-semibold text-ink">Venue spaces</h2>
            </div>

              <div v-if="spaces?.length" class="mt-4">
              <div class="flex flex-wrap gap-2">
                <button
                  v-for="item in spaces"
                  :key="item.id"
                  type="button"
                  class="rounded-full border px-4 py-2 text-sm font-semibold transition"
                  :class="selectedSpaceId === item.id ? 'border-[#d9a441] bg-[#fff7e8] text-[#80550d] shadow-sm' : 'border-[#eadfd4] bg-white text-[#6f5948] hover:border-[#d9a441] hover:text-[#80550d]'"
                  @click="selectedSpaceId = item.id"
                >
                  {{ item.spaceIcon }} {{ item.name }}
                </button>
              </div>

              <article v-if="selectedSpace" class="mt-4 overflow-hidden rounded-[22px] border border-[#eadfd4] bg-[#fffdfa]">
                <div class="flex flex-col gap-4 border-b border-[#eadfd4] px-4 py-4 lg:flex-row lg:items-start lg:justify-between">
                  <div class="min-w-0">
                    <h3 class="font-serif text-[1.65rem] font-semibold text-[#1f1208]">{{ selectedSpace.name }}</h3>
                    <div class="mt-3 flex flex-wrap gap-2">
                      <span class="rounded-full bg-[#f6efe8] px-3 py-1.5 text-sm font-semibold text-[#7a624e]">{{ selectedSpace.settingIcon }} {{ selectedSpace.setting || 'Indoor' }}</span>
                      <span class="rounded-full bg-[#f6efe8] px-3 py-1.5 text-sm font-semibold text-[#7a624e]">{{ selectedSpace.spaceTypeIcon }} {{ selectedSpace.spaceType || 'Space' }}</span>
                      <span v-if="selectedSpace.areaSqft" class="rounded-full bg-[#f6efe8] px-3 py-1.5 text-sm font-semibold text-[#7a624e]">📐 {{ selectedSpace.areaSqft }} sqft</span>
                    </div>
                  </div>

                  <div class="text-left lg:text-right">
                    <p class="text-[1.65rem] font-semibold leading-none text-[#1f1208]">{{ selectedSpace.priceDisplayLabel }}</p>
                    <p v-if="selectedSpace.displayMaxPrice" class="mt-2 text-base text-[#8b715e]">per event</p>
                  </div>
                </div>

                <div class="px-4 py-4">
                  <RichTextContent
                    class="text-[1rem] leading-7 text-[#2f241b]"
                    :html="selectedSpace.description"
                    fallback="No description added."
                  />

                  <div v-if="selectedSpace.seatingItems?.length" class="mt-5">
                    <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Seating configurations</p>
                    <div class="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                      <article v-for="item in selectedSpace.seatingItems || []" :key="`${selectedSpace.id}-${item.label}`" class="rounded-[16px] border border-[#eadfd4] bg-white px-3 py-3">
                        <div class="flex items-end justify-between gap-3">
                          <p class="text-[1rem] font-medium leading-6 text-[#2f241b]">{{ item.label }}</p>
                          <p class="text-lg font-semibold text-[#1f1208]">{{ item.capacity }}</p>
                        </div>
                      </article>
                    </div>
                  </div>

                  <div class="mt-5 grid gap-5 lg:grid-cols-2">
                    <div class="min-w-0">
                      <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Included amenities</p>
                      <div class="mt-3 space-y-2">
                        <div v-for="item in selectedSpace.includedAmenities || []" :key="`${selectedSpace.id}-amenity-${item}`" class="flex items-start justify-between gap-3 text-[1rem] text-[#2f241b]">
                          <span class="inline-flex items-center gap-3">
                            <span class="text-[#2e8a5d]">✓</span>
                            <span>{{ item }}</span>
                          </span>
                        </div>
                        <p v-if="!selectedSpace.includedAmenities?.length" class="text-sm text-muted">No included amenities added yet.</p>
                      </div>
                    </div>

                    <div class="min-w-0 hidden">
                      <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Equipment</p>
                      <div class="mt-3 space-y-2">
                        <div v-for="item in selectedSpace.equipmentItems || []" :key="`${selectedSpace.id}-equipment-${item.label}`" class="flex items-start justify-between gap-3 text-[1rem] text-[#2f241b]">
                          <span class="inline-flex items-center gap-3">
                            <span :class="item.included ? 'text-[#2e8a5d]' : 'text-[#c58a27]'">{{ item.included ? '✓' : '+' }}</span>
                            <span>{{ item.label }}</span>
                          </span>
                          <span :class="item.included ? 'text-[#2e8a5d]' : 'text-[#c58a27]'" class="text-sm font-semibold">
                            {{ item.included ? 'Included' : 'Optional' }}
                          </span>
                        </div>
                        <p v-if="!selectedSpace.equipmentItems?.length" class="text-sm text-muted">No equipment details added yet.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </article>
            </div>
          </section>

          <section v-if="packages?.length" class="rounded-[24px] border border-[#eadfd4] bg-white p-4 shadow-sm sm:p-5">
            <div>
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Packages</p>
              <h2 class="mt-2 font-serif text-[1.9rem] font-semibold text-ink">Venue packages</h2>
            </div>

            <div class="mt-4">
              <div class="flex flex-wrap gap-2">
                <button
                  v-for="item in packages"
                  :key="item.id"
                  type="button"
                  class="rounded-full border px-4 py-2 text-sm font-semibold transition"
                  :class="selectedPackageId === item.id ? 'border-[#d9a441] bg-[#fff7e8] text-[#80550d] shadow-sm' : 'border-[#eadfd4] bg-white text-[#6f5948] hover:border-[#d9a441] hover:text-[#80550d]'"
                  @click="selectedPackageId = item.id"
                >
                  <i class="ri-price-tag-3-line"></i>
                  {{ item.name }}
                </button>
              </div>

              <article v-if="selectedPackage" class="mt-4 overflow-hidden rounded-[22px] border border-[#eadfd4] bg-[#fffdfa]">
                <div class="flex flex-col gap-4 border-b border-[#eadfd4] px-4 py-4 lg:flex-row lg:items-start lg:justify-between">
                  <div class="min-w-0">
                    <h3 class="font-serif text-[1.65rem] font-semibold text-[#1f1208]">{{ selectedPackage.name }}</h3>
                    <div class="mt-3 flex flex-wrap gap-2">
                      <span class="rounded-full bg-[#f6efe8] px-3 py-1.5 text-sm font-semibold text-[#7a624e]">
                        <i class="ri-group-line"></i>
                        {{ selectedPackage.maxGuests || 'Flexible' }} guests
                      </span>
                      <span class="rounded-full bg-[#f6efe8] px-3 py-1.5 text-sm font-semibold text-[#7a624e]">
                        <i class="ri-time-line"></i>
                        {{ selectedPackage.validHours ? `${selectedPackage.validHours} hrs` : 'Flexible hours' }}
                      </span>
                      <span v-if="selectedPackage.includedServices?.length" class="rounded-full bg-[#f6efe8] px-3 py-1.5 text-sm font-semibold text-[#7a624e]">
                        <i class="ri-service-line"></i>
                        {{ selectedPackage.includedServices.length }} services
                      </span>
                    </div>
                  </div>

                  <div class="text-left lg:text-right">
                    <p class="text-[1.65rem] font-semibold leading-none text-[#1f1208]">{{ selectedPackage.priceLabel }}</p>
                    <p class="mt-2 text-base text-[#8b715e]">package price</p>
                  </div>
                </div>

                <div class="px-4 py-4">
                  <RichTextContent
                    class="text-[1rem] leading-7 text-[#2f241b]"
                    :html="selectedPackage.description"
                    fallback="No package summary added."
                  />

                  <div class="mt-5">
                    <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Included services</p>
                    <div v-if="selectedPackage.includedServices?.length" class="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                      <article v-for="service in selectedPackage.includedServices || []" :key="`${selectedPackage.id}-${service}`" class="rounded-[16px] border border-[#eadfd4] bg-white px-3 py-3">
                        <div class="flex items-center gap-3 text-[1rem] text-[#2f241b]">
                          <span class="text-[#2e8a5d]">✓</span>
                          <span>{{ service }}</span>
                        </div>
                      </article>
                    </div>
                    <p v-else class="mt-3 text-sm text-muted">No included services added yet.</p>
                  </div>
                </div>
              </article>
            </div>
          </section>

          <section v-if="hasTransportationDetails" class="rounded-[24px] border border-[#eadfd4] bg-white p-4 shadow-sm sm:p-5">
            <div>
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Transportation</p>
              <h2 class="mt-2 font-serif text-[1.9rem] font-semibold text-ink">Nearby access</h2>
            </div>

            <div class="mt-4">
              <div v-if="nearbyAreasList.length" class="mt-3 overflow-hidden rounded-[16px] border border-[#eadfd4]">
                <div v-for="item in nearbyAreasList" :key="item" class="flex items-center gap-3 border-b border-[#eadfd4] px-4 py-3 text-[1rem] text-[#2f241b] last:border-b-0">
                  <i class="ri-map-pin-2-fill text-[#d16c57]"></i>
                  <span>{{ item }}</span>
                </div>
              </div>
              <a
                v-if="transportationMapLink"
                :href="transportationMapLink"
                target="_blank"
                rel="noreferrer"
                class="mt-4 inline-flex items-center gap-2 rounded-full border border-[#eadfd4] bg-[#fffaf5] px-4 py-2 text-sm font-semibold text-brand transition hover:border-brand"
              >
                <i class="ri-road-map-line"></i>
                Open saved Google Maps link
              </a>
            </div>
          </section>

          <section v-if="menuPricingItems.length" class="rounded-[24px] border bg-white p-4 shadow-sm sm:p-5">
            <h2 class="text-sm font-semibold uppercase tracking-[0.14em] text-[#b78969]">Menu with Prices</h2>
            <p class="mt-2 text-sm leading-6 text-[#2f241b]">
              Below listed prices are <span class="font-semibold">excluding all taxes.</span>
            </p>
            <p class="mt-1 text-sm leading-6 text-[#2f241b]">
              Menu prices are subject to change and may vary depending on guest requirement and number of people. Please contact manager for more details.
            </p>

            <div v-if="menuPricingItems.length" class="mt-4 overflow-hidden rounded-[8px] border">
              <div class="grid grid-cols-[minmax(0,1fr)_112px] border-b bg-white text-sm font-semibold text-[#1f2a44] sm:grid-cols-[minmax(0,1fr)_160px]">
                <div class="border-r px-3 py-3">Menu Name</div>
                <div class="px-3 py-3">Per Person</div>
              </div>
              <div v-for="item in menuPricingItems" :key="item.key" class="grid grid-cols-[minmax(0,1fr)_112px] border-b text-sm last:border-b-0 sm:grid-cols-[minmax(0,1fr)_160px]">
                <div class="flex min-w-0 items-center gap-2 border-r px-3 py-3 text-[#1f2a44]">
                  <span :class="[item.swatchBorderClass, 'inline-flex h-3.5 w-3.5 items-center justify-center']">
                    <span :class="[item.swatchClass, 'h-1.5 w-1.5']"></span>
                  </span>
                  <span class="min-w-0 break-words">{{ item.displayName }}</span>
                </div>
                <div class="px-3 py-3 font-semibold text-[#1f2a44]">{{ item.price }}</div>
              </div>
            </div>
          </section>

          <section v-if="hasVenueAbout" class="rounded-[24px] border border-[#eadfd4] bg-white p-4 shadow-sm sm:p-5">
            <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">About</p>
            <h2 class="mt-2 font-serif text-[1.9rem] font-semibold text-ink">About this venue</h2>
            <RichTextContent class="mt-4 text-[1rem] leading-7 text-[#2f241b]" :html="venue.about" />
          </section>

          <section v-if="hasVenuePolicySection" class="rounded-[24px] border bg-white p-4 shadow-sm sm:p-5">
            <div>
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Policies</p>
              <h2 class="mt-2 font-serif text-[1.65rem] font-semibold text-ink sm:text-[1.9rem]">Venue information & policies</h2>
              <p class="mt-2 text-sm leading-6 text-muted">Key stay, timing, parking, catering, and booking rules in one place.</p>
            </div>

            <div class="mt-5 divide-y">
              <article v-if="hasRoomsDetails || hasVenueTimingDetails" class="px-1">
                <div class="grid gap-4 text-sm leading-5 text-[#2f241b] md:grid-cols-2">
                  <div v-if="hasRoomsDetails">
                    <h3 class="text-sm font-semibold uppercase tracking-[0.14em] text-[#b78969]">Rooms</h3>
                    <p class="mt-1 font-semibold text-[#2f241b]">{{ roomsAvailabilityLabel }}</p>
                  </div>
                  <div v-if="hasVenueTimingDetails">
                    <h3 class="text-sm font-semibold uppercase tracking-[0.14em] text-[#b78969]">Venue timings</h3>
                    <div class="mt-2 space-y-1">
                      <div v-for="item in venueTimingRows" :key="item.key" class="text-sm leading-6 text-[#2f241b]">
                        <span>{{ item.label }}: </span>
                        <span>{{ item.value }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </article>
            </div>

            <div v-if="policyRuleItems.length || bookingTermItems.length" class="grid gap-4 border-t px-1 pt-5 lg:grid-cols-2">
              <div v-if="policyRuleItems.length">
                <p class="text-sm font-semibold uppercase tracking-[0.14em] text-[#b78969]">Venue rules</p>
                <p class="mt-1 text-sm leading-5 text-[#6f5948]">House rules, service permissions, and restrictions guests should know before enquiring.</p>
                <div class="mt-3 space-y-3">
                  <article v-for="item in policyRuleItems" :key="item.label" class="flex items-start gap-2">
                    <span class="mt-0.5 text-lg">{{ item.icon }}</span>
                    <div class="min-w-0">
                      <p class="text-xs font-semibold uppercase tracking-[0.12em] text-[#b78969]">{{ item.label }}</p>
                      <div v-if="item.rows?.length" class="mt-1 space-y-1">
                        <div v-for="row in item.rows" :key="row.label" class="flex items-start gap-2 text-sm leading-5 text-[#2f241b]">
                          <span :class="row.available ? 'text-[#2e8a5d]' : 'text-[#d63f2f]'" class="mt-0.5 text-[1rem] leading-none">
                            <i :class="row.iconClass"></i>
                          </span>
                          <span>{{ row.label }}</span>
                        </div>
                      </div>
                      <template v-else>
                        <p class="mt-1 text-sm leading-5 text-[#2f241b]" :style="getDescriptionStyle(item)">
                          {{ item.value }}
                        </p>
                        <button
                          v-if="shouldShowDescriptionToggle(item)"
                          type="button"
                          class="mt-1 text-sm font-semibold text-brand hover:text-[#b88817]"
                          @click="togglePolicyDescription(item.key)"
                        >
                          {{ isPolicyDescriptionExpanded(item.key) ? 'Less' : 'More' }}
                        </button>
                      </template>
                    </div>
                  </article>
                </div>
              </div>

              <div v-if="bookingTermItems.length">
                <p class="text-sm font-semibold uppercase tracking-[0.14em] text-[#b78969]">Booking terms</p>
                <p class="mt-1 text-sm leading-5 text-[#6f5948]">Booking, availability, and cancellation details used while planning the event.</p>
                <div class="mt-3 space-y-3">
                  <article v-for="item in bookingTermItems" :key="item.label" class="flex items-start gap-2">
                    <span class="mt-0.5 text-lg">{{ item.icon }}</span>
                    <div class="min-w-0">
                      <p class="text-xs font-semibold uppercase tracking-[0.12em] text-[#b78969]">{{ item.label }}</p>
                      <template v-if="item.collapsible">
                        <p class="mt-1 text-sm leading-5 text-[#2f241b]" :style="getDescriptionStyle(item)">
                          {{ item.value }}
                        </p>
                        <button
                          v-if="shouldShowDescriptionToggle(item)"
                          type="button"
                          class="mt-1 text-sm font-semibold text-brand hover:text-[#b88817]"
                          @click="togglePolicyDescription(item.key)"
                        >
                          {{ isPolicyDescriptionExpanded(item.key) ? 'Less' : 'More' }}
                        </button>
                      </template>
                      <p v-else class="mt-1 text-sm leading-5 text-[#2f241b]">{{ item.value }}</p>
                    </div>
                  </article>
                </div>
              </div>
            </div>

            <div v-if="parkingDetailRows.length || additionalFacilitiesList.length || hasVenueHoursDetails" class="border-t px-1 pt-5">
              <article v-if="parkingDetailRows.length">
                <h3 class="text-sm font-semibold uppercase tracking-[0.14em] text-[#b78969]">Parking</h3>
                <p class="mt-1 text-sm leading-5 text-[#6f5948]">Parking capacity and valet support available at the property.</p>
                <div class="mt-2 space-y-1">
                  <div v-for="item in parkingDetailRows" :key="item.label" class="flex items-start gap-2 text-sm">
                    <span :class="item.available ? 'text-[#2e8a5d]' : 'text-[#d63f2f]'" class="mt-0.5 text-[1.05rem] leading-none">
                      <i :class="item.iconClass"></i>
                    </span>
                    <span class="leading-5 text-[#2f241b]">{{ item.label }}</span>
                  </div>
                </div>
              </article>

              <article v-if="additionalFacilitiesList.length" class="mt-5 border-t px-1 pt-5">
                <h3 class="text-sm font-semibold uppercase tracking-[0.14em] text-[#b78969]">Additional facilities</h3>
                <p class="mt-1 text-sm leading-5 text-[#6f5948]">Extra facilities marked by the venue for this listing.</p>
                <div class="mt-2 grid gap-x-5 md:grid-cols-2">
                  <div v-for="item in additionalFacilitiesList" :key="item.label" class="flex items-start gap-2 text-sm leading-5 text-[#2f241b]">
                    <span :class="item.available ? 'text-[#2e8a5d]' : 'text-[#d63f2f]'" class="mt-0.5 text-[1rem] leading-none">
                      <i :class="item.iconClass"></i>
                    </span>
                    <span>{{ item.label }}</span>
                  </div>
                </div>
              </article>

              <article v-if="hasVenueHoursDetails" class="mt-5 border-t px-1 pt-5">
                <h3 class="text-sm font-semibold uppercase tracking-[0.14em] text-[#b78969]">Venue hours</h3>
                <p class="mt-1 text-sm leading-5 text-[#6f5948]">Regular enquiry hours shared by the venue.</p>
                <p class="mt-2 text-sm font-semibold text-ink">Weekly enquiry hours</p>
                <div class="mt-1 grid gap-x-5 sm:grid-cols-2">
                  <div v-for="item in availabilityDayRows" :key="item.key" class="flex items-center justify-between gap-2 py-0.5 text-sm">
                    <span class="font-semibold text-[#2f241b]">{{ item.label }}</span>
                    <span class="text-[#6f5948]">{{ item.value }}</span>
                  </div>
                </div>
              </article>
            </div>
          </section>

          <section class="rounded-[24px] border border-[#eadfd4] bg-white p-4 shadow-sm sm:p-5">
            <div>
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Location</p>
              <h2 class="mt-2 font-serif text-[1.35rem] font-semibold leading-tight text-ink sm:text-[1.9rem]">{{ venue.address || 'Venue location' }}</h2>
            </div>

            <div class="mt-4 overflow-hidden rounded-[20px] border border-[#d8e7ef] bg-[#eef7fb]">
              <iframe
                v-if="locationGoogleMapsEmbedUrl"
                :src="locationGoogleMapsEmbedUrl"
                class="h-[280px] w-full border-0"
                loading="lazy"
                referrerpolicy="no-referrer-when-downgrade"
                :title="`Google map for ${venue.address || 'Venue location'}`"
              ></iframe>
              <div class="flex flex-col gap-3 border-t border-[#d8e7ef] bg-white px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
                <div class="min-w-0">
                  <p class="font-semibold leading-6 text-[#2f241b]">{{ venue.address || 'Venue location not added yet.' }}</p>
                  <p class="text-sm leading-5 text-[#5b6c76]">{{ venue.address || 'Location details unavailable' }}</p>
                </div>
                <a
                  v-if="locationGoogleMapsLink"
                  :href="locationGoogleMapsLink"
                  target="_blank"
                  rel="noreferrer"
                  class="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-[#c99a22] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#b88817]"
                >
                  Open in Google Maps
                  <i class="ri-arrow-right-up-line"></i>
                </a>
              </div>
            </div>
          </section>
        </div>

        <aside class="w-full min-w-0 self-start xl:sticky xl:top-24">
          <section class="rounded-[24px] border border-[#eadfd4] bg-white p-4 shadow-sm sm:p-5">
            <p class="text-sm font-semibold text-[#128018]">Availability checked 3 hr back</p>

            <form v-if="showEnquiryForm && !submittedEnquiry" class="mt-4 space-y-3 venue-enquiry-form" @submit.prevent="submitVenueEnquiry">
              <div v-if="!enquiryOtpSent" class="space-y-3">
              <div class="venue-field-shell">
                <i class="ri-calendar-event-line venue-field-icon"></i>
                <select v-model="enquiryForm.eventType" class="venue-enquiry-control venue-enquiry-select">
                  <option value="">Select Event Type</option>
                  <option v-for="item in eventTypeOptions" :key="item.value" :value="item.value">{{ item.label }}</option>
                </select>
                <i class="ri-arrow-down-s-line venue-field-chevron"></i>
              </div>
              <div class="venue-field-shell">
                <i class="ri-calendar-line venue-field-icon"></i>
                <input ref="enquiryDateInputRef" v-model="enquiryForm.eventDate" type="text" class="venue-enquiry-control venue-enquiry-date" placeholder="Select Event Date" readonly>
                <i class="ri-arrow-down-s-line venue-field-chevron"></i>
              </div>
              <div class="venue-field-shell">
                <i class="ri-time-line venue-field-icon"></i>
                <select v-model="enquiryForm.eventSession" class="venue-enquiry-control venue-enquiry-select">
                  <option value="">Select Event Session</option>
                  <option v-for="item in eventSessionOptions" :key="item.value" :value="item.label">{{ item.label }}</option>
                </select>
                <i class="ri-arrow-down-s-line venue-field-chevron"></i>
              </div>
              <div>
                <input v-model.trim="enquiryForm.name" type="text" :class="['venue-enquiry-control', enquiryFieldErrors.name ? 'venue-enquiry-control--error' : '']" placeholder="Full Name">
                <p v-if="enquiryFieldErrors.name" class="mt-1 text-xs font-medium text-red-600">{{ enquiryFieldErrors.name }}</p>
              </div>
              <div>
                <input ref="phoneInputRef" v-model.trim="enquiryForm.phone" type="tel" inputmode="numeric" maxlength="12" :class="['venue-enquiry-control', enquiryFieldErrors.phone ? 'venue-enquiry-control--error' : '']" placeholder="Mobile number" @input="limitPhoneDigits">
                <p v-if="enquiryFieldErrors.phone" class="mt-1 text-xs font-medium text-red-600">{{ enquiryFieldErrors.phone }}</p>
              </div>
              <div>
                <div class="venue-field-shell">
                  <i class="ri-mail-line venue-field-icon"></i>
                  <input v-model.trim="enquiryForm.email" type="email" :class="['venue-enquiry-control', enquiryFieldErrors.email ? 'venue-enquiry-control--error' : '']" placeholder="Email">
                </div>
                <p v-if="enquiryFieldErrors.email" class="mt-1 text-xs font-medium text-red-600">{{ enquiryFieldErrors.email }}</p>
              </div>
              <div>
                <input v-model.trim="enquiryForm.guestCount" type="text" inputmode="numeric" :class="['venue-enquiry-control', enquiryFieldErrors.guestCount ? 'venue-enquiry-control--error' : '']" placeholder="Guest count">
                <p v-if="enquiryFieldErrors.guestCount" class="mt-1 text-xs font-medium text-red-600">{{ enquiryFieldErrors.guestCount }}</p>
              </div>
              <div>
                <RichTextEditor
                  v-model="enquiryForm.details"
                  placeholder="Requirements"
                  :show-heading="false"
                  :show-links="false"
                  :class="['venue-enquiry-rich-editor', enquiryFieldErrors.details ? 'venue-enquiry-rich-editor--error' : '']"
                />
                <p v-if="enquiryFieldErrors.details" class="mt-1 text-xs font-medium text-red-600">{{ enquiryFieldErrors.details }}</p>
              </div>
              </div>

              <div v-else class="rounded-2xl border border-[#eadfd4] bg-[#fff7ef] p-4">
                <p class="text-xs font-semibold uppercase tracking-wide text-[#6f5948]">Email OTP</p>
                <p class="mt-1 text-xs text-[#7a6657]">Enter the 4 digit code sent to {{ enquiryOtpEmail }}.</p>
                <div class="mt-3 grid grid-cols-4 gap-2">
                  <input
                    v-for="(_, index) in enquiryOtpDigits"
                    :key="`venue-otp-${index}`"
                    :ref="(el) => setVenueOtpInputRef(el, index)"
                    v-model="enquiryOtpDigits[index]"
                    type="text"
                    inputmode="numeric"
                    maxlength="1"
                    class="venue-enquiry-otp"
                    :class="{ 'venue-enquiry-control--error': enquiryFieldErrors.otp }"
                    @input="handleVenueOtpInput(index)"
                    @keydown="handleVenueOtpKeydown($event, index)"
                    @paste="handleVenueOtpPaste"
                  >
                </div>
                <p v-if="enquiryFieldErrors.otp" class="mt-2 text-xs font-medium text-red-600">{{ enquiryFieldErrors.otp }}</p>
              </div>

              <p v-if="enquiryError" class="rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
                {{ enquiryError }}
              </p>
              <p v-if="enquirySuccess" class="rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700">
                {{ enquirySuccess }}
              </p>

              <button
                type="submit"
                :disabled="enquiryLoading"
                class="w-full rounded-none bg-[#ff310f] px-4 py-3 text-sm font-semibold text-white transition hover:bg-[#dd270b] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {{ enquiryLoading ? (enquiryOtpSent ? 'Verifying...' : 'Checking...') : (enquiryOtpSent ? 'Verify & Submit Enquiry' : 'Check Availability') }}
              </button>
            </form>

            <div v-else-if="submittedEnquiry" class="mt-4 space-y-3">
              <div class="rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700">
                {{ enquirySuccess || 'Enquiry sent successfully.' }}
              </div>
              <div class="rounded-2xl border border-[#eadfd4] bg-[#fffaf5] p-4 text-sm text-[#6f5948]">
                <p class="font-semibold text-ink">{{ submittedEnquiry.name }}</p>
                <p class="mt-1">{{ submittedEnquiry.phoneCode }} {{ submittedEnquiry.phone }}</p>
                <p class="mt-1">{{ submittedEnquiry.email }}</p>
                <div class="mt-3 grid gap-2 border-t border-[#eadfd4] pt-3">
                  <p><span class="font-semibold text-ink">Event:</span> {{ submittedEnquiry.eventType || 'N/A' }}</p>
                  <p><span class="font-semibold text-ink">Session:</span> {{ submittedEnquiry.eventSession || 'N/A' }}</p>
                  <p><span class="font-semibold text-ink">Date:</span> {{ submittedEnquiry.eventDate || 'N/A' }}</p>
                  <p><span class="font-semibold text-ink">Guests:</span> {{ submittedEnquiry.guestCount || 'N/A' }}</p>
                  <div>
                    <span class="font-semibold text-ink">Requirements:</span>
                    <RichTextContent
                      class="mt-1 text-sm leading-6 text-[#6f5948]"
                      :html="submittedEnquiry.details"
                      fallback="N/A"
                    />
                  </div>
                </div>
              </div>
              <button
                type="button"
                class="w-full rounded-none border border-[#cfcfcf] bg-white px-4 py-3 text-sm font-semibold text-[#1f2a44] transition hover:border-brand hover:text-brand"
                @click="startAnotherEnquiry"
              >
                Send another enquiry
              </button>
            </div>
            <div v-else class="mt-4 space-y-3 rounded-2xl border border-[#eadfd4] bg-[#fffaf5] p-4 text-sm text-[#6f5948]">
              <p class="font-semibold text-ink">Enquiry is currently disabled for this venue.</p>
              <p class="mt-2 leading-6">You can still contact the venue directly using the details below.</p>
              <div class="mt-3 space-y-2 border-t border-[#eadfd4] pt-3">
                <p v-if="venueManagerPhone !== 'Contact not added'" class="font-semibold text-ink">
                  <i class="ri-phone-fill align-[-1px]"></i>
                  {{ venueManagerPhone }}
                </p>
                <p v-if="venue.email" class="font-semibold text-ink">
                  <i class="ri-mail-fill align-[-1px]"></i>
                  {{ venue.email }}
                </p>
                <a v-if="venue.website" :href="venue.website" target="_blank" rel="noopener" class="inline-flex font-semibold text-brand hover:text-brandDark">
                  Open website
                </a>
              </div>
            </div>
          </section>
        </aside>
      </section>
    </div>
  </section>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import intlTelInput from 'intl-tel-input'
import allCountries from 'intl-tel-input/data'
import flatpickr from 'flatpickr'
import 'flatpickr/dist/flatpickr.css'
import lightGallery from 'lightgallery'
import lgThumbnail from 'lightgallery/plugins/thumbnail'
import lgZoom from 'lightgallery/plugins/zoom'
import RichTextContent from '@/components/common/RichTextContent.vue'
import RichTextEditor from '@/components/common/RichTextEditor.vue'
import api from '@/services/user/api'
import vendorApi from '@/services/vendor/api'
import Swal from 'sweetalert2'

const route = useRoute()

const loading = ref(false)
const error = ref('')
const venue = ref({
  id: '',
  title: '',
  tagLine: '',
  about: '',
  address: '',
  latitude: '',
  longitude: '',
  phone: '',
  phoneCode: '',
  email: '',
  website: '',
  typeLabel: 'Venue',
  yearEstablished: '',
  eventsHosted: [],
  highlights: [],
  amenities: [],
  inHouseServices: [],
  additionalFacilities: [],
  additionalFacilitiesStatuses: [],
  noOfRooms: '',
  parkingAvailable: '',
  valetParking: '',
  parkingVehicleCount: '',
  morningTimingFrom: '',
  morningTimingTo: '',
  eveningTimingFrom: '',
  eveningTimingTo: '',
  sessions: [],
  nearbyAreas: [],
  transportationMapLink: '',
  availability: {}
})
const gallery = ref([])
const spaces = ref([])
const selectedSpaceId = ref('')
const packages = ref([])
const selectedPackageId = ref('')
const expandedPolicyDescriptions = ref({})
const inviteGalleryLightbox = ref(null)
const enquiryEligibility = ref(1)
const venueEventHostedOptions = ref([])
const phoneInputRef = ref(null)
const phoneInputInstance = ref(null)
const enquiryDateInputRef = ref(null)
const enquiryDatePicker = ref(null)
const enquiryLoading = ref(false)
const enquiryError = ref('')
const enquirySuccess = ref('')
const enquiryFieldErrors = ref({})
const submittedEnquiry = ref(null)
const enquiryOtpSent = ref(false)
const enquiryOtpEmail = ref('')
const venueEnquiryId = ref('')
const enquiryOtpDigits = ref(['', '', '', ''])
const venueOtpInputRefs = ref([])
const enquiryForm = ref({
  name: '',
  email: '',
  phoneCode: '+91',
  phone: '',
  eventType: '',
  eventSession: '',
  eventDate: '',
  guestCount: '',
  details: ''
})
const policies = ref({
  inHouseCatering: '',
  outsideCatering: '',
  outsideCateringFeeType: '',
  outsideCateringFeeValue: '',
  cateringFoodType: '',
  cateringPriceUnit: '',
  pricingBasisCount: '',
  nonVegPricingBasisCount: '',
  outsideCateringFoodType: '',
  outsideCateringPriceUnit: '',
  outsidePricingBasisCount: '',
  outsideNonVegPricingBasisCount: '',
  decorPolicy: '',
  musicPolicy: '',
  alcoholInhouse: '',
  alcoholOutside: '',
  minGuests: '',
  maxGuests: '',
  decorTime: '',
  cleanTime: '',
  noiseMusic: '',
  otherHouseRules: '',
  cancelTerms: '',
  depositPercent: ''
})
const getListItemLabel = (item) => {
  if (item == null) return ''
  if (typeof item !== 'object') return String(item).trim()
  return String(
    item.name ||
    item.label ||
    item.title ||
    item.facility ||
    item.facility_name ||
    item.amenity ||
    item.amenity_name ||
    item.value ||
    ''
  ).trim()
}

const parseListString = (value) => {
  const raw = String(value || '').trim()
  if (!raw) return []
  if ((raw.startsWith('[') && raw.endsWith(']')) || (raw.startsWith('{') && raw.endsWith('}'))) {
    try {
      return splitToArray(JSON.parse(raw))
    } catch {
      // Fall back to delimiter parsing.
    }
  }
  return raw
    .split(/[,|\n]/)
    .map((item) => item.trim())
    .filter(Boolean)
}

const splitToArray = (value) => {
  if (Array.isArray(value)) return value.flatMap((item) => splitToArray(item))
  if (value && typeof value === 'object') {
    const label = getListItemLabel(value)
    return label ? [label] : []
  }
  return parseListString(value)
}

const hiddenAmenityLabels = new Set(['swimming pool', 'spa', 'business center'])
const filterVisibleAmenityLabels = (items = []) =>
  items.map((item) => String(item || '').trim()).filter((item) => item && !hiddenAmenityLabels.has(item.toLowerCase()))

const getListSource = (...values) => values.find((value) => {
  if (Array.isArray(value)) return value.length > 0
  if (value && typeof value === 'object') return Object.keys(value).length > 0
  return String(value ?? '').trim() !== ''
}) ?? ''

const splitSessionValues = (value) => String(value || '').split(',').map((entry) => entry.trim())

const getFirstValue = (...values) => values.find((value) => String(value ?? '').trim() !== '') ?? ''
const normalizeExternalUrl = (value) => {
  const raw = String(value || '').trim()
  if (!raw) return ''
  if (/^https?:\/\//i.test(raw)) return raw
  if (/^www\./i.test(raw)) return `https://${raw}`
  return ''
}
const buildGoogleMapsLink = (value) => {
  const raw = String(value || '').trim()
  if (!raw) return ''
  return normalizeExternalUrl(raw) || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(raw)}`
}
const stripHtml = (value) => {
  if (typeof document === 'undefined') return String(value || '').replace(/<[^>]+>/g, ' ')
  const container = document.createElement('div')
  container.innerHTML = String(value || '')
  return container.textContent || container.innerText || ''
}
const normalizeDialCode = (value) => String(value || '').replace(/[^\d]/g, '')
const resolveCountryFromDialCode = (value) => {
  const dialCode = normalizeDialCode(value)
  if (!dialCode) return 'in'
  return allCountries.find((country) => country.dialCode === dialCode)?.iso2 || 'in'
}
const getVendorIdFromPayload = (payload = {}) => String(
  payload?.vendor_id ||
  payload?.vendorId ||
  payload?.s_id ||
  payload?.seller_id ||
  payload?.vendor?.id ||
  payload?.vendor?.s_id ||
  ''
).trim()
const normalizeEnquiryEligibility = (value, fallback = 1) => {
  if (value === undefined || value === null || value === '') return fallback
  return Number(value) === 0 ? 0 : 1
}

const normalizeVenueSessionTypeValue = (value) => {
  const normalized = String(value || '').trim().toLowerCase()
  if (!normalized) return ''
  if (normalized.includes('request')) return 'on-request'
  if (normalized.includes('dinner') || normalized.includes('night') || normalized.includes('evening')) return 'evening'
  if (normalized.includes('morning') || normalized.includes('lunch') || normalized.includes('afternoon')) return 'morning'
  return normalized
}

const buildVenueTimingSessions = (item = {}) => {
  const types = splitSessionValues(item?.session_type)
  const fromTimes = splitSessionValues(item?.session_from_time)
  const toTimes = splitSessionValues(item?.session_to_time)
  const total = Math.max(types.length, fromTimes.length, toTimes.length, 0)
  const morningFrom = getFirstValue(item?.session_from_time_mrng, item?.mrng_from_time, item?.venue_timing_morning_from, item?.morning_timing_from)
  const morningTo = getFirstValue(item?.session_to_time_mrng, item?.mrng_to_time, item?.venue_timing_morning_to, item?.morning_timing_to)
  const eveningFrom = getFirstValue(item?.session_from_time_night, item?.evening_from_time, item?.venue_timing_evening_from, item?.evening_timing_from)
  const eveningTo = getFirstValue(item?.session_to_time_night, item?.evening_to_time, item?.venue_timing_evening_to, item?.evening_timing_to)

  const sessions = Array.from({ length: total }, (_, index) => ({
    type: normalizeVenueSessionTypeValue(types[index]),
    fromTime: getFirstValue(fromTimes[index], normalizeVenueSessionTypeValue(types[index]) === 'morning' ? morningFrom : '', normalizeVenueSessionTypeValue(types[index]) === 'evening' ? eveningFrom : ''),
    toTime: getFirstValue(toTimes[index], normalizeVenueSessionTypeValue(types[index]) === 'morning' ? morningTo : '', normalizeVenueSessionTypeValue(types[index]) === 'evening' ? eveningTo : '')
  })).filter((session) => session.type || session.fromTime || session.toTime)

  if ((morningFrom || morningTo) && !sessions.some((session) => session.type === 'morning')) {
    sessions.push({ type: 'morning', fromTime: morningFrom, toTime: morningTo })
  }
  if ((eveningFrom || eveningTo) && !sessions.some((session) => session.type === 'evening')) {
    sessions.push({ type: 'evening', fromTime: eveningFrom, toTime: eveningTo })
  }
  if (isYesValue(item?.session_on_request) && !sessions.some((session) => session.type === 'on-request')) {
    sessions.push({ type: 'on-request', fromTime: '', toTime: '' })
  }

  return sessions
}

const normalizeVenueAvailability = (item = {}) => ({
  status: String(item?.status ?? '').trim(),
  leadTime: getFirstValue(item?.lead_time, item?.leadTime),
  maxAdvanceBooking: getFirstValue(item?.max_advance_booking, item?.maxAdvanceBooking),
  monFromTime: getFirstValue(item?.mon_from_time, item?.monday_from_time),
  monToTime: getFirstValue(item?.mon_to_time, item?.monday_to_time),
  tuesFromTime: getFirstValue(item?.tues_from_time, item?.tuesday_from_time),
  tuesToTime: getFirstValue(item?.tues_to_time, item?.tuesday_to_time),
  wedFromTime: getFirstValue(item?.wed_from_time, item?.wednesday_from_time),
  wedToTime: getFirstValue(item?.wed_to_time, item?.wednesday_to_time),
  thursFromTime: getFirstValue(item?.thurs_from_time, item?.thursday_from_time),
  thursToTime: getFirstValue(item?.thurs_to_time, item?.thursday_to_time),
  fridFromTime: getFirstValue(item?.frid_from_time, item?.friday_from_time),
  fridToTime: getFirstValue(item?.frid_to_time, item?.friday_to_time),
  satFromTime: getFirstValue(item?.sat_from_time, item?.saturday_from_time),
  satToTime: getFirstValue(item?.sat_to_time, item?.saturday_to_time),
  sunFromTime: getFirstValue(item?.sun_from_time, item?.sunday_from_time),
  sunToTime: getFirstValue(item?.sun_to_time, item?.sunday_to_time)
})

const formatCurrency = (value) => {
  if (String(value ?? '').trim() === '') return ''
  const numeric = Number(String(value || '').replace(/[^\d.]/g, ''))
  return Number.isFinite(numeric) && numeric >= 0 ? `₹${numeric.toLocaleString('en-IN')}` : ''
}

const hasPositivePrice = (value) => {
  if (String(value ?? '').trim() === '') return false
  const numeric = Number(String(value || '').replace(/[^\d.]/g, ''))
  return Number.isFinite(numeric) && numeric > 0
}

const formatTimeTo12Hour = (value) => {
  const rawValue = String(value || '').trim()
  const match = rawValue.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/)
  if (!match) return rawValue
  const hour = Number(match[1])
  const minute = match[2]
  if (!Number.isFinite(hour) || hour < 0 || hour > 23) return rawValue
  const period = hour >= 12 ? 'PM' : 'AM'
  const displayHour = hour % 12 || 12
  return `${displayHour}:${minute} ${period}`
}

const formatTimeRange = (fromTime, toTime) => {
  const fromLabel = formatTimeTo12Hour(fromTime)
  const toLabel = formatTimeTo12Hour(toTime)
  if (fromLabel && toLabel) return `${fromLabel} to ${toLabel}`
  return fromLabel || toLabel || 'Time not added'
}

const formatPriceRange = (minPrice, maxPrice) => {
  const minValue = formatCurrency(minPrice)
  const maxValue = hasPositivePrice(maxPrice) ? formatCurrency(maxPrice) : ''
  if (minValue && maxValue) return `${minValue} - ${maxValue}`
  return minValue || maxValue || 'Price on request'
}
const formatSpacePriceLabel = (minPrice, maxPrice) => {
  const minValue = formatCurrency(minPrice)
  const maxValue = hasPositivePrice(maxPrice) ? formatCurrency(maxPrice) : ''
  if (minValue && maxValue) return `${minValue} - ${maxValue}`
  if (minValue) return `Price Starts from ${minValue}`
  if (maxValue) return maxValue
  return 'Price on request'
}

const formatYesNo = (value) => {
  const normalized = String(value || '').trim().toLowerCase()
  if (!normalized) return ''
  if (['1', 'yes', 'true'].includes(normalized)) return 'Yes'
  if (['0', 'no', 'false'].includes(normalized)) return 'No'
  return String(value || '').trim()
}

const isYesValue = (value) => formatYesNo(value).toLowerCase() === 'yes'

const getStatusState = (value, fallback = null) => {
  const normalized = String(value ?? '').trim().toLowerCase()
  if (!normalized) return fallback
  if (['0', 'no', 'false', 'off'].includes(normalized) || /not available|not provided|unavailable|not allowed|do not|no\b/.test(normalized)) return false
  if (['1', 'yes', 'true', 'on'].includes(normalized) || /available|allowed|provided|permit|partner|panel|approved|own kitchen|welcome/.test(normalized)) return true
  return fallback
}

const getStatusIconClass = (available) => available ? 'ri-checkbox-circle-fill' : 'ri-close-circle-fill'

const formatStatusPolicyValue = (value, label) => {
  const available = getStatusState(value)
  const text = String(value || '').trim()
  if (available === null && !text) return ''
  return `${label}${text && text !== label ? `: ${text}` : ''}`
}

const formatStatusPolicyRow = (value, label) => {
  const available = getStatusState(value)
  if (available === null) return null
  return {
    label,
    iconClass: getStatusIconClass(available),
    available
  }
}

const parseBothFoodPricing = (value) => {
  const parsed = {
    veg: '',
    nonVeg: ''
  }

  String(value || '').split('||').map((item) => item.trim()).filter(Boolean).forEach((part) => {
    const [rawKey, rawValue] = part.split('::')
    const key = String(rawKey || '').trim().toLowerCase()
    const price = String(rawValue || '').trim()
    if (key === 'veg') parsed.veg = price
    if (key === 'nonveg') parsed.nonVeg = price
  })

  return parsed
}

const inferSpaceIcon = (value) => {
  const normalized = String(value || '').trim().toLowerCase()
  if (!normalized) return '🏛️'
  if (normalized.includes('lawn') || normalized.includes('garden')) return '🌿'
  if (normalized.includes('terrace') || normalized.includes('rooftop')) return '⛱️'
  if (normalized.includes('pool')) return '🏊'
  if (normalized.includes('outdoor')) return '☀️'
  if (normalized.includes('indoor')) return '🏠'
  if (normalized.includes('hall') || normalized.includes('ballroom')) return '🏛️'
  return '🏛️'
}

const normalizeSpace = (item = {}, index = 0) => {
  const configurations = splitToArray(item?.seat_config || item?.seatConfig)
  const capacities = splitToArray(item?.seat_capacity || item?.seatCapacity)
  const amenities = filterVisibleAmenityLabels(splitToArray(item?.amenities))
  const spaceType = item?.space_type || item?.spaceType || item?.type_name || item?.type || ''
  const setting = item?.setting || 'Indoor'
  const seatingItems = configurations.map((label, configIndex) => ({
    label,
    capacity: capacities[configIndex] || 'N/A'
  }))
  const includedAmenities = amenities.slice(0, 6)
  const equipmentItems = amenities.slice(6).map((label) => ({
    label,
    included: true
  }))

  return {
    id: String(item?.space_id || item?.id || `space-${index}`),
    name: item?.name || 'Untitled space',
    spaceType,
    setting,
    spaceIcon: inferSpaceIcon(spaceType || item?.name || ''),
    spaceTypeIcon: inferSpaceIcon(spaceType || ''),
    settingIcon: inferSpaceIcon(setting),
    areaSqft: item?.area_sqft || item?.areaSqft || '',
    description: item?.description || '',
    minPrice: item?.min_price || item?.minPrice || '',
    maxPrice: item?.max_price || item?.maxPrice || '',
    displayMinPrice: formatCurrency(item?.min_price || item?.minPrice) || 'Price on request',
    displayMaxPrice: hasPositivePrice(item?.max_price || item?.maxPrice) ? formatCurrency(item?.max_price || item?.maxPrice) : '',
    priceDisplayLabel: formatSpacePriceLabel(item?.min_price || item?.minPrice, item?.max_price || item?.maxPrice),
    priceLabel: formatPriceRange(item?.min_price || item?.minPrice, item?.max_price || item?.maxPrice),
    seatingItems,
    includedAmenities,
    equipmentItems,
    seatingSummary: configurations.map((label, configIndex) => {
      const capacity = capacities[configIndex]
      return capacity ? `${label}: ${capacity}` : label
    })
  }
}

const normalizeGallery = (items = []) => {
  const source = Array.isArray(items) ? items : [items]
  return source
    .flatMap((item, itemIndex) => {
      if (!item) return []
      if (typeof item === 'string') {
        return [{
          id: `gallery-${itemIndex}-${item}`,
          src: String(item).trim(),
          isCover: false
        }]
      }
      const imageKeys = ['image1', 'image2', 'image3', 'image4', 'image', 'image_url', 'url', 'path', 'img', 'src']
      const entryId = String(item?.id || item?.gallery_id || item?.venue_gallery_id || item?.img_id || '')
      return imageKeys
        .map((key, keyIndex) => {
          const src = String(item?.[key] || '').trim()
          if (!src) return null
          return {
            id: entryId || `gallery-${itemIndex}-${keyIndex}-${src}`,
            src,
            isCover: String(item?.is_cover || item?.isCover || '0') === '1'
          }
        })
        .filter(Boolean)
    })
    .sort((left, right) => Number(right.isCover) - Number(left.isCover))
}

const VISIBLE_GALLERY_TILE_COUNT = 4

const lightboxGalleryImages = computed(() => {
  const sortedGallery = [...gallery.value].sort((left, right) => Number(right.isCover) - Number(left.isCover))
  const filledTiles = sortedGallery.slice(0, VISIBLE_GALLERY_TILE_COUNT).map((item, index) => ({
    ...item,
    id: item.id || `preview-tile-${index}`,
    overlayText: ''
  }))

  const hiddenCount = Math.max(0, sortedGallery.length - VISIBLE_GALLERY_TILE_COUNT)
  if (filledTiles[VISIBLE_GALLERY_TILE_COUNT - 1]) {
    filledTiles[VISIBLE_GALLERY_TILE_COUNT - 1] = {
      ...filledTiles[VISIBLE_GALLERY_TILE_COUNT - 1],
      overlayText: hiddenCount ? `+${hiddenCount}` : ''
    }
  }

  return filledTiles
})

const heroGallery = computed(() => lightboxGalleryImages.value.slice(0, VISIBLE_GALLERY_TILE_COUNT))
const venueManagerPhone = computed(() => {
  const phone = String(venue.value.phone || '').trim()
  return phone ? `${venue.value.phoneCode ? `${venue.value.phoneCode} ` : ''}${phone}` : 'Contact not added'
})
const venueHighlightsList = computed(() => {
  const items = Array.isArray(venue.value.highlights) ? venue.value.highlights : []
  const normalized = items.map((item) => String(item || '').trim()).filter(Boolean)
  return normalized.slice(0, 8)
})
const inHouseServicesList = computed(() => {
  const packageServices = packages.value.flatMap((item) => item.includedServices || [])
  const venueServices = Array.isArray(venue.value.inHouseServices) ? venue.value.inHouseServices : []
  const servicePool = [...venueServices, ...packageServices].map((item) => String(item || '').trim()).filter(Boolean)
  const uniqueServices = [...new Set(servicePool)]
  return uniqueServices.slice(0, 8)
})
const additionalFacilitiesList = computed(() => {
  const facilities = filterVisibleAmenityLabels(Array.isArray(venue.value.additionalFacilities) ? venue.value.additionalFacilities : [])
  const amenities = filterVisibleAmenityLabels(Array.isArray(venue.value.amenities) ? venue.value.amenities : [])
  const items = [...new Set([...facilities, ...amenities])]
  const statuses = Array.isArray(venue.value.additionalFacilitiesStatuses) ? venue.value.additionalFacilitiesStatuses : []
  return items.map((item, index) => {
    const available = getStatusState(statuses[index], getStatusState(item, true))
    return {
      label: item,
      available,
      iconClass: getStatusIconClass(available)
    }
  }).filter((item) => item.label)
})
const nearbyAreasList = computed(() => venue.value.nearbyAreas.filter(Boolean))
const transportationMapLink = computed(() => buildGoogleMapsLink(venue.value.transportationMapLink))
const venueLocationQuery = computed(() => {
  const latitude = String(venue.value.latitude || '').trim()
  const longitude = String(venue.value.longitude || '').trim()
  return latitude && longitude ? `${latitude},${longitude}` : String(venue.value.address || '').trim()
})
const locationGoogleMapsLink = computed(() => transportationMapLink.value || buildGoogleMapsLink(venueLocationQuery.value))
const locationGoogleMapsEmbedUrl = computed(() => venueLocationQuery.value ? `https://www.google.com/maps?q=${encodeURIComponent(venueLocationQuery.value)}&output=embed` : '')
const parkingDetailRows = computed(() => {
  const carParking = formatYesNo(venue.value.parkingAvailable)
  const valetParking = formatYesNo(venue.value.valetParking)
  const vehicleCount = String(venue.value.parkingVehicleCount || '').trim()
  const rows = []

  if (carParking) {
    const available = isYesValue(venue.value.parkingAvailable)
    rows.push({ label: available ? 'Car parking available' : 'Car parking not available', available, iconClass: getStatusIconClass(available) })
  }
  if (isYesValue(venue.value.parkingAvailable) && vehicleCount) {
    rows.push({ label: `Parking capacity: ${vehicleCount} vehicles`, available: true, iconClass: getStatusIconClass(true) })
  }
  if (valetParking) {
    const available = isYesValue(venue.value.valetParking)
    rows.push({
      label: available ? 'Valet parking available' : 'Valet parking not available',
      available,
      iconClass: getStatusIconClass(available)
    })
  }

  return rows
})
const roomsAvailabilityLabel = computed(() => {
  const rawRooms = String(venue.value.noOfRooms || '').trim()
  const roomCount = Number(rawRooms)
  if (!rawRooms) return 'N/A'
  if (Number.isFinite(roomCount) && roomCount <= 0) return 'N/A'
  if (Number.isFinite(roomCount)) return `${roomCount} ${roomCount === 1 ? 'room' : 'rooms'} available`
  return rawRooms
})
const hasRoomsDetails = computed(() => roomsAvailabilityLabel.value !== 'N/A')
const isInHouseCateringUnavailable = computed(() => {
  const policy = String(policies.value.inHouseCatering || '').toLowerCase()
  return policy.includes('no') || policy.includes('do not serve')
})
const venueFoodAllowanceRow = computed(() => {
  if (isInHouseCateringUnavailable.value) return null
  const normalized = String(policies.value.cateringFoodType || '').trim().toLowerCase()
  if (!normalized) return null
  const label = normalized.includes('both') || (normalized.includes('veg') && normalized.includes('non'))
    ? 'Both Veg & Non-Veg food allowed at the venue'
    : normalized.includes('veg')
      ? 'Veg only food allowed at the venue'
      : normalized.includes('non')
        ? 'Non-Veg food allowed at the venue'
        : ''
  return label ? { label, available: true, iconClass: getStatusIconClass(true) } : null
})
const availabilitySummaryItems = computed(() => {
  const availability = venue.value.availability || {}
  const leadTime = String(availability.leadTime || '').trim()
  const maxAdvanceBooking = String(availability.maxAdvanceBooking || '').trim()
  const status = String(availability.status || '').trim()
  return [
    { label: 'Booking status', value: status === '1' ? 'Accepting enquiries' : status === '0' ? 'Not accepting enquiries' : 'N/A' },
    { label: 'Lead time', value: hasPositiveNumericValue(leadTime) ? `${leadTime} ${leadTime === '1' ? 'day' : 'days'}` : 'N/A' },
    { label: 'Advance booking', value: hasPositiveNumericValue(maxAdvanceBooking) ? `Up to ${maxAdvanceBooking} ${maxAdvanceBooking === '1' ? 'month' : 'months'}` : 'N/A' }
  ]
})
const availabilityDayRows = computed(() => {
  const availability = venue.value.availability || {}
  return [
    { key: 'mon', label: 'Monday', from: availability.monFromTime, to: availability.monToTime },
    { key: 'tues', label: 'Tuesday', from: availability.tuesFromTime, to: availability.tuesToTime },
    { key: 'wed', label: 'Wednesday', from: availability.wedFromTime, to: availability.wedToTime },
    { key: 'thurs', label: 'Thursday', from: availability.thursFromTime, to: availability.thursToTime },
    { key: 'frid', label: 'Friday', from: availability.fridFromTime, to: availability.fridToTime },
    { key: 'sat', label: 'Saturday', from: availability.satFromTime, to: availability.satToTime },
    { key: 'sun', label: 'Sunday', from: availability.sunFromTime, to: availability.sunToTime }
  ]
    .map((item) => ({ ...item, value: formatTimeRange(item.from, item.to) }))
    .filter((item) => item.from || item.to)
})
const eventTypeOptions = computed(() => {
  const options = venueEventHostedOptions.value.length
    ? venueEventHostedOptions.value
    : Array.isArray(venue.value.eventsHosted)
      ? venue.value.eventsHosted.map(normalizeEventTypeOption).filter((item) => item.value !== '')
      : []
  return options.length
    ? options
    : [
        { value: 1, label: 'Wedding' },
        { value: 2, label: 'Reception' },
        { value: 3, label: 'Birthday' },
        { value: 4, label: 'Corporate Event' }
      ]
})
const eventSessionOptions = computed(() => {
  const sessions = venueTimingRows.value
    .filter((item) => item.key !== 'on-request')
    .map((item) => ({ value: item.key, label: item.label }))
  const options = [
    { value: 'full-day', label: 'Full Day' },
    ...sessions,
    { value: 'morning', label: 'Morning / Lunch' },
    { value: 'evening', label: 'Dinner / Night' }
  ]
  return options.filter((item, index, list) => list.findIndex((option) => option.value === item.value) === index)
})
// A backend eligibility value must not hide the public enquiry workflow.
// It can still be loaded for compatibility, but both preview and invite detail
// always expose the same enquiry form.
const showEnquiryForm = computed(() => true)
const alcoholPolicySummary = computed(() => {
  const items = [
    formatStatusPolicyValue(policies.value.alcoholInhouse, 'Alcohol served by venue'),
    formatStatusPolicyValue(policies.value.alcoholOutside, 'Outside alcohol allowed')
  ].filter(Boolean)

  return items.join(' · ') || 'N/A'
})
const formatOutsideCatererVenueFee = () => {
  const feeType = String(policies.value.outsideCateringFeeType || '').trim()
  const feeValue = String(policies.value.outsideCateringFeeValue || '').trim()
  if (!feeType || feeType.toLowerCase() === 'no fee') return ''
  return `Outside caterer venue fee: ${feeType}${feeValue ? ` ₹${feeValue}` : ''}`
}
const venueTimingRows = computed(() => {
  const sessionByType = (type) => (Array.isArray(venue.value.sessions) ? venue.value.sessions : []).find((session) => session.type === type)
  const morningSession = sessionByType('morning')
  const eveningSession = sessionByType('evening')
  const requestSession = sessionByType('on-request')
  if (requestSession) {
    return [{ key: 'on-request', label: 'Timing', value: 'On Request' }]
  }

  const morningStart = morningSession?.fromTime || venue.value.morningTimingFrom || ''
  const morningEnd = morningSession?.toTime || venue.value.morningTimingTo || ''
  const eveningStart = eveningSession?.fromTime || venue.value.eveningTimingFrom || ''
  const eveningEnd = eveningSession?.toTime || venue.value.eveningTimingTo || ''
  const rows = []

  if (morningSession || morningStart || morningEnd) {
    rows.push({ key: 'morning', label: 'Morning / Lunch', value: formatTimeRange(morningStart, morningEnd) })
  }
  if (eveningSession || eveningStart || eveningEnd) {
    rows.push({ key: 'evening', label: 'Dinner / Night', value: formatTimeRange(eveningStart, eveningEnd) })
  }

  return rows
})
const hasVenueTimingDetails = computed(() => venueTimingRows.value.length > 0)
const hasTransportationDetails = computed(() => nearbyAreasList.value.length > 0 || Boolean(transportationMapLink.value))
const buildMenuPricingRows = ({ policy, foodType, pricingCount, nonVegPricingCount, unitLabel, keyPrefix }) => {
  const normalizedFoodType = foodType.toLowerCase()
  const bothPricing = parseBothFoodPricing(pricingCount)
  const hasBoth = normalizedFoodType.includes('both')
  const rows = []

  if (hasBoth) {
    if (bothPricing.veg || pricingCount) rows.push({ name: 'Veg', rawPrice: bothPricing.veg || pricingCount, swatchBorderClass: 'border border-[#1f9d45]', swatchClass: 'bg-[#1f9d45]' })
    if (bothPricing.nonVeg || nonVegPricingCount) rows.push({ name: 'Non Veg', rawPrice: bothPricing.nonVeg || nonVegPricingCount, swatchBorderClass: 'border border-[#d45711]', swatchClass: 'bg-[#d45711]' })
  } else if (normalizedFoodType.includes('non')) {
    rows.push({ name: 'Non Veg', rawPrice: pricingCount, swatchBorderClass: 'border border-[#d45711]', swatchClass: 'bg-[#d45711]' })
  } else if (normalizedFoodType.includes('veg')) {
    rows.push({ name: 'Veg', rawPrice: pricingCount, swatchBorderClass: 'border border-[#1f9d45]', swatchClass: 'bg-[#1f9d45]' })
  } else if (pricingCount) {
    rows.push({ name: 'Menu', rawPrice: pricingCount, swatchBorderClass: 'border border-[#b78969]', swatchClass: 'bg-[#b78969]' })
  }

  return rows
    .filter((item) => String(item.rawPrice || '').trim())
    .map((item, index) => ({
      ...item,
      key: `${keyPrefix}-${index}-${item.name}`,
      policy,
      displayName: item.name,
      unitLabel: unitLabel || 'Per Person',
      price: formatCurrency(item.rawPrice) || String(item.rawPrice || '').trim()
    }))
}
const menuPricingItems = computed(() => {
  if (isInHouseCateringUnavailable.value) return []
  const rows = buildMenuPricingRows({
    policy: 'In-house catering',
    foodType: String(policies.value.cateringFoodType || '').trim(),
    pricingCount: String(policies.value.pricingBasisCount || '').trim(),
    nonVegPricingCount: String(policies.value.nonVegPricingBasisCount || '').trim(),
    unitLabel: policies.value.cateringPriceUnit,
    keyPrefix: 'in-house'
  })
  return rows
})
const selectedSpace = computed(() => {
  if (!spaces.value.length) return null
  return spaces.value.find((item) => item.id === selectedSpaceId.value) || spaces.value[0]
})
const selectedPackage = computed(() => {
  if (!packages.value.length) return null
  return packages.value.find((item) => item.id === selectedPackageId.value) || packages.value[0]
})

const venueRuleItems = computed(() => [
  {
    icon: '🍽️',
    label: 'Food',
    rows: [
      formatStatusPolicyRow(policies.value.inHouseCatering, 'In-house catering'),
      venueFoodAllowanceRow.value,
      formatStatusPolicyRow(policies.value.outsideCatering, 'Outside catering'),
      formatOutsideCatererVenueFee()
        ? { label: formatOutsideCatererVenueFee(), available: true, iconClass: getStatusIconClass(true) }
        : null
    ].filter(Boolean),
    value: 'N/A'
  },
  { icon: '🌸', label: 'Decoration', value: policies.value.decorPolicy || 'N/A' },
  { icon: '🎵', label: 'DJ / Music', value: policies.value.musicPolicy || 'N/A' },
  {
    icon: '🥂',
    label: 'Alcohol',
    rows: [
      formatStatusPolicyRow(policies.value.alcoholInhouse, 'Alcohol served by venue'),
      formatStatusPolicyRow(policies.value.alcoholOutside, 'Outside alcohol allowed')
    ].filter(Boolean),
    value: alcoholPolicySummary.value
  },
  { icon: '🔊', label: 'Noise curfew', value: policies.value.noiseMusic ? `Music must end by ${policies.value.noiseMusic}.` : 'N/A' },
  {
    icon: '📋',
    key: 'house-rules',
    label: 'House rules',
    value: policies.value.otherHouseRules || 'N/A',
    collapsible: Boolean(policies.value.otherHouseRules)
  }
])

const policyRuleItems = computed(() => venueRuleItems.value.filter((item) =>
  item.rows?.length || (String(item.value || '').trim() && String(item.value || '').trim() !== 'N/A')
))

const hasPositiveNumericValue = (value) => {
  if (String(value ?? '').trim() === '') return false
  const numeric = Number(String(value || '').replace(/[^\d.]/g, ''))
  return Number.isFinite(numeric) && numeric > 0
}

const formatGuestCountLabel = () => {
  const minGuests = String(policies.value.minGuests || '').trim()
  const maxGuests = String(policies.value.maxGuests || '').trim()
  const hasMinGuests = hasPositiveNumericValue(minGuests)
  const hasMaxGuests = hasPositiveNumericValue(maxGuests)
  if (hasMinGuests && hasMaxGuests) return `${minGuests} - ${maxGuests} guests`
  if (hasMinGuests) return `From ${minGuests} guests`
  if (hasMaxGuests) return `Up to ${maxGuests} guests`
  return ''
}

const bookingTermItems = computed(() => [
  ...availabilitySummaryItems.value.map((item) => ({
    icon: item.label === 'Booking status' ? '📌' : item.label === 'Lead time' ? '⏱️' : '📅',
    label: item.label,
    value: item.value
  })),
  { icon: '👥', label: 'Guest count', value: formatGuestCountLabel() },
  { icon: '🛠️', label: 'Setup time', value: policies.value.decorTime || 'N/A' },
  { icon: '🧹', label: 'Cleanup time', value: policies.value.cleanTime || 'N/A' },
  { icon: '💳', label: 'Deposit', value: hasPositiveNumericValue(policies.value.depositPercent) ? `${policies.value.depositPercent}% of total` : 'N/A' },
  {
    icon: '↩️',
    key: 'cancellation',
    label: 'Cancellation',
    value: policies.value.cancelTerms || 'N/A',
    collapsible: Boolean(policies.value.cancelTerms)
  }
].filter((item) => String(item.value || '').trim() && String(item.value || '').trim() !== 'N/A'))
const hasVenueHoursDetails = computed(() => availabilityDayRows.value.length > 0)
const hasVenueAbout = computed(() => Boolean(stripHtml(venue.value.about).trim()))
const hasVenuePolicySection = computed(() =>
  hasRoomsDetails.value ||
  hasVenueTimingDetails.value ||
  policyRuleItems.value.length > 0 ||
  bookingTermItems.value.length > 0 ||
  parkingDetailRows.value.length > 0 ||
  additionalFacilitiesList.value.length > 0 ||
  hasVenueHoursDetails.value
)

const isPolicyDescriptionExpanded = (key) => Boolean(expandedPolicyDescriptions.value[key])
const shouldShowDescriptionToggle = (item) => Boolean(item?.collapsible && String(item.value || '').trim().length > 120)
const togglePolicyDescription = (key) => {
  expandedPolicyDescriptions.value = {
    ...expandedPolicyDescriptions.value,
    [key]: !expandedPolicyDescriptions.value[key]
  }
}
const getDescriptionStyle = (item) => {
  if (!item?.collapsible || isPolicyDescriptionExpanded(item.key)) return {}
  return {
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: '2',
    overflow: 'hidden'
  }
}

const breadcrumbCategory = computed(() => venue.value.typeLabel || 'Venue')

const destroyInviteGallery = () => {
  if (inviteGalleryLightbox.value) {
    inviteGalleryLightbox.value.destroy(true)
    inviteGalleryLightbox.value = null
  }
}

const syncPhoneCountryCode = () => {
  const countryData = phoneInputInstance.value?.getSelectedCountryData?.()
  const dialCode = countryData?.dialCode ? `+${countryData.dialCode}` : enquiryForm.value.phoneCode || '+91'
  enquiryForm.value.phoneCode = dialCode
}

const initPhoneInput = async () => {
  await nextTick()
  if (!phoneInputRef.value || phoneInputInstance.value) return
  phoneInputInstance.value = intlTelInput(phoneInputRef.value, {
    initialCountry: resolveCountryFromDialCode(enquiryForm.value.phoneCode),
    separateDialCode: true,
    nationalMode: true,
    autoPlaceholder: 'polite',
    loadUtils: () => import('intl-tel-input/utils')
  })
  phoneInputRef.value.addEventListener('countrychange', syncPhoneCountryCode)
  syncPhoneCountryCode()
}

const destroyPhoneInput = () => {
  if (phoneInputRef.value) {
    phoneInputRef.value.removeEventListener('countrychange', syncPhoneCountryCode)
  }
  phoneInputInstance.value?.destroy?.()
  phoneInputInstance.value = null
}

const initEnquiryDatePicker = async () => {
  await nextTick()
  if (!enquiryDateInputRef.value || enquiryDatePicker.value) return
  enquiryDatePicker.value = flatpickr(enquiryDateInputRef.value, {
    altInput: true,
    altInputClass: 'venue-enquiry-control venue-enquiry-date',
    altFormat: 'd M Y',
    dateFormat: 'Y-m-d',
    minDate: 'today',
    disableMobile: true,
    allowInput: false,
    monthSelectorType: 'static',
    nextArrow: '<i class="ri-arrow-right-s-line"></i>',
    prevArrow: '<i class="ri-arrow-left-s-line"></i>',
    onChange: ([selectedDate], dateStr) => {
      enquiryForm.value.eventDate = selectedDate ? dateStr : ''
    },
    onReady: (selectedDates, dateStr, instance) => {
      instance.calendarContainer.classList.add('venue-enquiry-calendar')
    }
  })
}

const destroyEnquiryDatePicker = () => {
  enquiryDatePicker.value?.destroy?.()
  enquiryDatePicker.value = null
}

const initEnquiryControls = async () => {
  await Promise.all([initPhoneInput(), initEnquiryDatePicker()])
}

const openInviteGallery = (startIndex = 0) => {
  if (!lightboxGalleryImages.value.length) return
  destroyInviteGallery()
  inviteGalleryLightbox.value = lightGallery(document.createElement('div'), {
    dynamic: true,
    dynamicEl: lightboxGalleryImages.value.map((image) => ({
      src: image.src,
      thumb: image.src
    })),
    index: Math.max(0, startIndex),
    plugins: [lgThumbnail, lgZoom],
    download: false,
    actualSize: false
  })
  inviteGalleryLightbox.value.openGallery(startIndex)
}

const openInviteGalleryById = (image) => {
  const imageId = String(image?.id || '')
  const startIndex = lightboxGalleryImages.value.findIndex((item) => String(item.id || '') === imageId)
  openInviteGallery(startIndex >= 0 ? startIndex : 0)
}

const extractVenueDetailPayload = (data) => {
  const isVenueRecord = (value) => value && typeof value === 'object' && [
    'id',
    'venue_id',
    'title',
    'venue_name',
    'address',
    'gallery_info',
    'spaces_info',
    'venue_policies'
  ].some((key) => Object.prototype.hasOwnProperty.call(value, key))
  const isApiEnvelope = (value) => value && typeof value === 'object' && !isVenueRecord(value) && [
    'status',
    'msg',
    'message',
    'success',
    'api',
    'errors'
  ].some((key) => Object.prototype.hasOwnProperty.call(value, key))
  const candidates = [
    data?.data,
    data?.venue,
    data?.item,
    data?.detail,
    data?.details,
    data?.venue_detail,
    data?.venueDetail,
    data
  ]
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      const first = candidate.find((item) => item && typeof item === 'object' && Object.keys(item).length)
      if (first) return first
    }
    if (candidate && typeof candidate === 'object') {
      const nested = candidate.venue || candidate.item || candidate.detail || candidate.details || candidate.venue_detail || candidate.venueDetail
      if (Array.isArray(nested)) {
        const first = nested.find((item) => item && typeof item === 'object' && Object.keys(item).length)
        if (first) return first
      }
      if (nested && typeof nested === 'object' && Object.keys(nested).length) return nested
      if (isVenueRecord(candidate)) return candidate
      if (Object.keys(candidate).length && !isApiEnvelope(candidate)) return candidate
    }
  }
  return null
}

const fetchVenueDetailByParams = async ({ venueId, vendorId = '', businessId = '' }) => {
  const formData = new FormData()
  formData.append('venue_id', venueId)
  if (vendorId) formData.append('vendor_id', vendorId)
  if (businessId) formData.append('business_id', businessId)
  const { data } = await api.getVenueDetailInvite(formData)
  return data
}

const loadVenueDetailPayload = async () => {
  const venueId = String(route.params.venueId || '')
  const queryVendorId = String(route.query.vendor_id || route.query.vendorId || '')
  const queryBusinessId = String(
    route.params.businessId ||
    route.params.id ||
    route.query.business_id ||
    route.query.businessId ||
    ''
  )
  const attempts = [
    { venueId, vendorId: queryVendorId, businessId: queryBusinessId },
    { venueId, vendorId: queryVendorId },
    { venueId, businessId: queryBusinessId },
    { venueId }
  ]
  const seen = new Set()
  let lastData = null
  for (const attempt of attempts) {
    const key = JSON.stringify(attempt)
    if (seen.has(key)) continue
    seen.add(key)
    const data = await fetchVenueDetailByParams(attempt)
    lastData = data
    const payload = extractVenueDetailPayload(data)
    if (payload && Object.keys(payload).length) {
      return {
        data,
        payload,
        vendorId: attempt.vendorId,
        businessId: attempt.businessId
      }
    }
  }
  return {
    data: lastData,
    payload: null,
    vendorId: queryVendorId,
    businessId: queryBusinessId
  }
}

const showEnquiryAlert = (icon, title, text) => Swal.fire({
  icon,
  title,
  text,
  confirmButtonColor: '#ff6b00'
})

const extractArrayPayload = (data, keys = []) => {
  const payload = data?.data || data || {}
  if (Array.isArray(payload)) return payload
  for (const key of keys) {
    if (Array.isArray(payload?.[key])) return payload[key]
  }
  return []
}

const normalizeEventTypeOption = (item) => {
  if (item == null) return { value: '', label: '' }
  if (typeof item !== 'object') {
    const raw = String(item).trim()
    const numericValue = Number(raw)
    return {
      value: Number.isFinite(numericValue) ? numericValue : '',
      label: raw
    }
  }
  const rawValue = item.id ?? item.event_id ?? item.event_type_id ?? item.hosted_event_id ?? item.value ?? ''
  const numericValue = Number(rawValue)
  return {
    value: Number.isFinite(numericValue) ? numericValue : '',
    label: String(item.name || item.label || item.title || item.event_name || item.value || '').trim()
  }
}

const loadVenueEventHostedOptions = async () => {
  try {
    const { data } = await vendorApi.getVenueEventsHosted(new FormData())
    const options = extractArrayPayload(data, ['events', 'venue_events_hosted', 'items', 'list'])
      .map(normalizeEventTypeOption)
      .filter((item) => item.value !== '' && item.label)
    venueEventHostedOptions.value = options.filter((item, index, list) => list.findIndex((option) => option.value === item.value) === index)
  } catch {
    venueEventHostedOptions.value = []
  }
}

const loadEnquiryEligibility = async (vendorId, fallbackValue = 1) => {
  enquiryEligibility.value = normalizeEnquiryEligibility(fallbackValue, 1)
  if (!vendorId) return
  try {
    const formData = new FormData()
    formData.append('vendor_id', vendorId)
    const { data } = await vendorApi.getProfileVendor(formData)
    const profile = data?.user || data?.data?.user || data?.data || data?.profile || data || {}
    enquiryEligibility.value = normalizeEnquiryEligibility(
      profile?.enquire_eligibility ?? data?.enquire_eligibility,
      enquiryEligibility.value
    )
  } catch {
    // Keep the venue-detail value when profile refresh is unavailable.
  }
}

const getPlainText = (value) => {
  const container = document.createElement('div')
  container.innerHTML = String(value || '')
  return (container.textContent || '').trim()
}

const validateVenueEnquiryForm = () => {
  const nextErrors = {}
  const email = String(enquiryForm.value.email || '').trim()
  const phone = String(enquiryForm.value.phone || '').replace(/\D/g, '')
  const guestCount = String(enquiryForm.value.guestCount || '').trim()

  if (!String(enquiryForm.value.name || '').trim()) nextErrors.name = 'Please enter your full name.'
  if (!phone) nextErrors.phone = 'Please enter your mobile number.'
  else if (!/^\d{10,12}$/.test(phone)) nextErrors.phone = 'Please enter a 10 to 12 digit mobile number.'
  if (!email) nextErrors.email = 'Please enter your email address.'
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) nextErrors.email = 'Please enter a valid email address.'
  if (guestCount && !/^\d+$/.test(guestCount)) nextErrors.guestCount = 'Guest count should be a number.'
  if (!getPlainText(enquiryForm.value.details)) nextErrors.details = 'Please add your requirements.'

  enquiryFieldErrors.value = nextErrors
  return Object.keys(nextErrors).length === 0
}

const limitPhoneDigits = () => {
  enquiryForm.value.phone = String(enquiryForm.value.phone || '').replace(/\D/g, '').slice(0, 12)
}

const enquiryOtpCode = computed(() => enquiryOtpDigits.value.join(''))

const resetVenueEnquiryOtp = () => {
  enquiryOtpSent.value = false
  enquiryOtpEmail.value = ''
  venueEnquiryId.value = ''
  enquiryOtpDigits.value = ['', '', '', '']
  venueOtpInputRefs.value = []
}

const setVenueOtpInputRef = (el, index) => {
  if (el) venueOtpInputRefs.value[index] = el
}

const focusVenueOtpInput = async (index) => {
  await nextTick()
  venueOtpInputRefs.value[index]?.focus?.()
}

const handleVenueOtpInput = (index) => {
  enquiryOtpDigits.value[index] = String(enquiryOtpDigits.value[index] || '').replace(/\D/g, '').slice(0, 1)
  enquiryFieldErrors.value = { ...enquiryFieldErrors.value, otp: '' }
  if (enquiryOtpDigits.value[index] && index < enquiryOtpDigits.value.length - 1) {
    focusVenueOtpInput(index + 1)
  }
}

const handleVenueOtpKeydown = (event, index) => {
  if (event.key === 'Backspace' && !enquiryOtpDigits.value[index] && index > 0) {
    focusVenueOtpInput(index - 1)
  }
}

const handleVenueOtpPaste = (event) => {
  event.preventDefault()
  const digits = String(event.clipboardData?.getData('text') || '').replace(/\D/g, '').slice(0, 4).split('')
  enquiryOtpDigits.value = ['', '', '', ''].map((_, index) => digits[index] || '')
  focusVenueOtpInput(Math.min(digits.length, 3))
}

const getEnquirySnapshot = () => ({
  name: enquiryForm.value.name,
  email: enquiryForm.value.email,
  phoneCode: enquiryForm.value.phoneCode || '+91',
  phone: enquiryForm.value.phone,
  eventType: eventTypeOptions.value.find((item) => String(item.value) === String(enquiryForm.value.eventType))?.label || '',
  eventSession: enquiryForm.value.eventSession,
  eventDate: enquiryForm.value.eventDate,
  guestCount: enquiryForm.value.guestCount,
  details: enquiryForm.value.details
})

const resetEnquiryForm = () => {
  enquiryForm.value = {
    name: '',
    email: '',
    phoneCode: '+91',
    phone: '',
    eventType: '',
    eventSession: '',
    eventDate: '',
    guestCount: '',
    details: ''
  }
  enquiryDatePicker.value?.clear?.()
  resetVenueEnquiryOtp()
}

const startAnotherEnquiry = async () => {
  submittedEnquiry.value = null
  enquirySuccess.value = ''
  enquiryError.value = ''
  enquiryFieldErrors.value = {}
  resetEnquiryForm()
  await initEnquiryControls()
}

const buildVenueEnquiryFormData = () => {
  const formData = new FormData()
  formData.append('venue_id', venue.value.id || String(route.params.venueId || ''))
  if (venue.value.vendorId) formData.append('vendor_id', venue.value.vendorId)
  formData.append('name', enquiryForm.value.name)
  formData.append('email', enquiryForm.value.email)
  formData.append('phone_code', enquiryForm.value.phoneCode || '+91')
  formData.append('phone', enquiryForm.value.phone)
  formData.append('details', enquiryForm.value.details)
  formData.append('guests_count', enquiryForm.value.guestCount || '')
  formData.append('session', enquiryForm.value.eventSession || '')
  formData.append('event_date', enquiryForm.value.eventDate || '')
  formData.append('event_type', enquiryForm.value.eventType !== '' ? Number(enquiryForm.value.eventType) : '')
  return formData
}

const verifyVenueEnquiryOtp = async () => {
  const otp = enquiryOtpCode.value
  if (!/^\d{4}$/.test(otp)) {
    enquiryFieldErrors.value = { ...enquiryFieldErrors.value, otp: 'Enter the 4 digit OTP.' }
    return false
  }
  const formData = new FormData()
  formData.append('enquiry_id', venueEnquiryId.value)
  formData.append('otp', otp)
  const { data } = await api.verifyVenueEnquiry(formData)
  const isError = data?.status === 0 || data?.status === false || data?.status === 'error' || data?.success === false
  if (isError) throw new Error(data?.msg || data?.message || 'Invalid OTP.')
  return true
}

const createVenueEnquiry = async () => {
  const { data } = await api.sendVenueEnquiry(buildVenueEnquiryFormData())
  const isError = data?.status === 0 || data?.status === 'error' || data?.success === false
  if (isError) {
    throw new Error(data?.msg || data?.message || 'Unable to send enquiry.')
  }
  const enquiryId = data?.enquiry_id || data?.data?.enquiry_id || data?.data?.id || data?.id || ''
  if (!enquiryId) throw new Error(data?.msg || data?.message || 'Enquiry ID was not returned.')
  venueEnquiryId.value = String(enquiryId)
  enquiryOtpSent.value = true
  enquiryOtpEmail.value = enquiryForm.value.email
  enquiryOtpDigits.value = ['', '', '', '']
  enquirySuccess.value = data?.message || data?.msg || 'OTP sent to your email.'
  await focusVenueOtpInput(0)
}

const submitVenueEnquiry = async () => {
  enquiryError.value = ''
  enquirySuccess.value = ''

  if (!validateVenueEnquiryForm()) {
    enquiryError.value = 'Please fix the highlighted fields before sending your enquiry.'
    await showEnquiryAlert('warning', 'Check enquiry details', enquiryError.value)
    return
  }

  enquiryLoading.value = true
  try {
    if (!enquiryOtpSent.value || !venueEnquiryId.value) {
      await createVenueEnquiry()
      return
    }
    const verified = await verifyVenueEnquiryOtp()
    if (!verified) return
    submittedEnquiry.value = getEnquirySnapshot()
    enquirySuccess.value = 'Enquiry submitted successfully.'
    enquiryFieldErrors.value = {}
    destroyPhoneInput()
    destroyEnquiryDatePicker()
    resetVenueEnquiryOtp()
    await showEnquiryAlert('success', 'Enquiry sent', enquirySuccess.value)
  } catch (err) {
    enquiryError.value = err?.response?.data?.msg || err?.response?.data?.message || err?.message || 'Unable to verify OTP.'
    await showEnquiryAlert('error', 'Unable to verify OTP', enquiryError.value)
  } finally {
    enquiryLoading.value = false
  }
}

const loadVenueDetail = async () => {
  loading.value = true
  error.value = ''
  try {
    const { data, payload, vendorId: requestedVendorId } = await loadVenueDetailPayload()
    if (!payload || !Object.keys(payload).length) {
      error.value = data?.msg || data?.message || 'Venue details could not be loaded for this link.'
      return
    }

    const vendorId = getVendorIdFromPayload(payload) || String(requestedVendorId || '').trim()
    const facilityRows = Array.isArray(payload?.additional_facilities) ? payload.additional_facilities : []
    const facilityNames = facilityRows.length
      ? facilityRows.map((item) => getListItemLabel(item)).filter(Boolean)
      : splitToArray(getListSource(payload?.additionalFacilities, payload?.additional_facilities_info, payload?.facilities, payload?.facility_info, payload?.venue_facilities))
    const facilityStatuses = facilityRows.length
      ? facilityRows.map((item) => String(item?.status ?? '').trim())
      : splitToArray(getListSource(payload?.additional_facilities_status, payload?.additionalFacilitiesStatus, payload?.additional_facilities_status_info, payload?.facilities_status, payload?.venue_facilities_status))
    venue.value = {
      id: String(payload?.venue_id || payload?.id || route.params.venueId || ''),
      vendorId,
      title: payload?.title || payload?.venue_name || payload?.name || 'Untitled venue',
      tagLine: payload?.tag_line || payload?.tagline || '',
      about: payload?.about || payload?.description || '',
      address: payload?.fulladdress || payload?.address || 'Location unavailable',
      latitude: payload?.latitude || payload?.lat || '',
      longitude: payload?.longitude || payload?.lng || '',
      phone: payload?.phone || payload?.mobile || payload?.contact_number || '',
      phoneCode: payload?.phone_code || '',
      email: payload?.email || '',
      website: normalizeExternalUrl(payload?.website || ''),
      typeLabel: payload?.type_name || payload?.venue_type_name || payload?.type || payload?.venue_type || 'Venue',
      yearEstablished: payload?.est_year || payload?.year_established || '',
      eventsHosted: splitToArray(payload?.events_hosted || payload?.eventsHosted),
      highlights: splitToArray(payload?.highlights || payload?.venue_highlights),
      amenities: filterVisibleAmenityLabels(splitToArray(getListSource(payload?.amenities, payload?.venue_amenities, payload?.amenities_info, payload?.venue_amenities_info))),
      inHouseServices: splitToArray(payload?.in_house_services || payload?.inHouseServices),
      additionalFacilities: facilityNames,
      additionalFacilitiesStatuses: facilityStatuses,
      noOfRooms: payload?.no_of_rooms || payload?.rooms_count || '',
      parkingAvailable: getFirstValue(payload?.car_parking_available, payload?.car_parking_avail, payload?.parking_available),
      valetParking: payload?.wallet_parking || payload?.valet_parking || '',
      parkingVehicleCount: getFirstValue(payload?.parking_vehicle_count, payload?.no_of_vehicles),
      morningTimingFrom: getFirstValue(payload?.venue_timing_morning_from, payload?.morning_timing_from, payload?.mrng_from_time),
      morningTimingTo: getFirstValue(payload?.venue_timing_morning_to, payload?.morning_timing_to, payload?.mrng_to_time),
      eveningTimingFrom: getFirstValue(payload?.venue_timing_evening_from, payload?.evening_timing_from, payload?.evening_from_time),
      eveningTimingTo: getFirstValue(payload?.venue_timing_evening_to, payload?.evening_timing_to, payload?.evening_to_time),
      sessions: buildVenueTimingSessions(payload),
      nearbyAreas: splitToArray(payload?.nearby_areas || payload?.transportation_nearby_areas),
      transportationMapLink: payload?.transportation_map_link || payload?.google_maps_link || payload?.map_link || '',
      availability: normalizeVenueAvailability(payload?.venue_availability || payload?.availability || payload?.venueAvailability || {})
    }
    await loadEnquiryEligibility(vendorId, payload?.enquire_eligibility)

    gallery.value = normalizeGallery(payload?.gallery_info || payload?.gallery || payload?.images || [])
    const spaceRows = Array.isArray(payload?.spaces_info)
      ? payload.spaces_info
      : payload?.spaces_info?.spaces || payload?.spaces_info?.items || payload?.spaces || []
    spaces.value = Array.isArray(spaceRows)
      ? spaceRows.map((item, index) => normalizeSpace(item, index))
      : []
    selectedSpaceId.value = spaces.value[0]?.id || ''

    packages.value = Array.isArray(payload?.venue_packages)
      ? payload.venue_packages.map((item, index) => ({
          id: String(item?.id || `package-${index}`),
          name: item?.name || 'Untitled package',
          description: item?.description || '',
          maxGuests: item?.max_guests || '',
          validHours: item?.valid_hours || '',
          includedServices: splitToArray(item?.included_services),
          priceLabel: formatCurrency(item?.price) || 'Price on request'
        }))
      : []
    selectedPackageId.value = packages.value[0]?.id || ''

    policies.value = {
      inHouseCatering: payload?.venue_policies?.in_house_catering || '',
      outsideCatering: payload?.venue_policies?.outside_catering || '',
      outsideCateringFeeType: payload?.venue_policies?.outside_catering_fee_type || payload?.venue_policies?.outside_fee_type || '',
      outsideCateringFeeValue: payload?.venue_policies?.outside_catering_fee_value || payload?.venue_policies?.outside_fee_value || '',
      cateringFoodType: payload?.venue_policies?.catering_food_type || payload?.venue_policies?.food_type || payload?.venue_policies?.veg_non_veg || '',
      cateringPriceUnit: payload?.venue_policies?.catering_price_unit || payload?.venue_policies?.price_unit || payload?.venue_policies?.price_per || payload?.venue_policies?.pricing_basis || '',
      pricingBasisCount: payload?.venue_policies?.pricing_basis_count || payload?.venue_policies?.count || '',
      nonVegPricingBasisCount: payload?.venue_policies?.pricing_basis_count_nonveg || payload?.venue_policies?.nonveg_count || '',
      outsideCateringFoodType: payload?.venue_policies?.food_type_outside || '',
      outsideCateringPriceUnit: payload?.venue_policies?.pricing_basis_outside || '',
      outsidePricingBasisCount: payload?.venue_policies?.pricing_basis_count_outside || '',
      outsideNonVegPricingBasisCount: payload?.venue_policies?.pricing_basis_count_nonveg_outside || '',
      decorPolicy: payload?.venue_policies?.decor_policy || '',
      musicPolicy: payload?.venue_policies?.music_policy || '',
      alcoholInhouse: String(payload?.venue_policies?.alcohol_inhouse || ''),
      alcoholOutside: String(payload?.venue_policies?.alcohol_outside || ''),
      minGuests: payload?.venue_policies?.min_guests || '',
      maxGuests: payload?.venue_policies?.max_guests || '',
      decorTime: payload?.venue_policies?.decor_time || '',
      cleanTime: payload?.venue_policies?.clean_time || '',
      noiseMusic: payload?.venue_policies?.noise_music || '',
      otherHouseRules: payload?.venue_policies?.other_house_rules || '',
      cancelTerms: payload?.venue_policies?.cancel_terms || '',
      depositPercent: payload?.venue_policies?.deposit_percent || ''
    }

    if (data?.status !== 'ok' && data?.msg) {
      if (/not available|not found|invalid/i.test(String(data.msg || ''))) {
        error.value = data.msg
        return
      }
      error.value = data.msg
    }
  } catch (err) {
    const message = err?.message || 'Failed to load venue details.'
    if (/not available|not found|invalid/i.test(String(message))) {
      error.value = message
      return
    }
    error.value = message
  } finally {
    loading.value = false
    if (showEnquiryForm.value) initEnquiryControls()
  }
}

onMounted(() => {
  loadVenueEventHostedOptions()
  loadVenueDetail()
})

watch(
  () => route.fullPath,
  (nextPath, previousPath) => {
    if (previousPath && nextPath !== previousPath) loadVenueDetail()
  }
)

onBeforeUnmount(() => {
  destroyInviteGallery()
  destroyPhoneInput()
  destroyEnquiryDatePicker()
})
</script>

<style scoped>
.venue-field-shell {
  position: relative;
}

.venue-field-icon,
.venue-field-chevron {
  pointer-events: none;
  position: absolute;
  top: 50%;
  z-index: 2;
  transform: translateY(-50%);
  color: #9a7a63;
  font-size: 1rem;
}

.venue-field-icon {
  left: 0.9rem;
}

.venue-field-chevron {
  right: 0.85rem;
}

.venue-enquiry-control {
  width: 100%;
  min-height: 46px;
  border: 1px solid #e3d4c7;
  border-radius: 14px;
  background-color: #fff;
  padding: 0.78rem 0.95rem;
  color: #1f2a44;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.25rem;
  box-shadow: 0 1px 0 rgba(15, 23, 42, 0.02);
  transition: border-color 0.18s ease, box-shadow 0.18s ease, background-color 0.18s ease;
}

.venue-field-shell .venue-enquiry-control {
  padding-left: 2.45rem;
  padding-right: 2.35rem;
}

.venue-field-shell :deep(.venue-enquiry-control) {
  width: 100%;
  min-height: 46px;
  border: 1px solid #e3d4c7;
  border-radius: 14px;
  background-color: #fff;
  padding: 0.78rem 2.35rem 0.78rem 2.45rem;
  color: #1f2a44;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.25rem;
  box-shadow: 0 1px 0 rgba(15, 23, 42, 0.02);
  transition: border-color 0.18s ease, box-shadow 0.18s ease, background-color 0.18s ease;
}

.venue-enquiry-control::placeholder {
  color: #98a2b3;
  font-weight: 500;
}

.venue-enquiry-control:focus {
  border-color: #fb7210;
  box-shadow: 0 0 0 3px rgba(251, 114, 16, 0.12);
  outline: none;
}

.venue-field-shell :deep(.venue-enquiry-control:focus) {
  border-color: #fb7210;
  box-shadow: 0 0 0 3px rgba(251, 114, 16, 0.12);
  outline: none;
}

.venue-enquiry-control--error {
  border-color: #f87171;
  background-color: #fff7f7;
}

.venue-enquiry-otp {
  min-height: 46px;
  border: 1px solid #e3d4c7;
  border-radius: 14px;
  background: #fffdfa;
  text-align: center;
  font-size: 18px;
  font-weight: 700;
  color: #1f1208;
  outline: none;
}

.venue-enquiry-otp:focus {
  border-color: #fb7210;
  box-shadow: 0 0 0 3px rgba(251, 114, 16, 0.12);
}

.venue-enquiry-select {
  appearance: none;
  cursor: pointer;
}

.venue-enquiry-date {
  cursor: pointer;
}

.venue-enquiry-textarea {
  min-height: 108px;
  resize: vertical;
}

.venue-enquiry-rich-editor {
  margin-top: 0;
  border-color: #e3d4c7;
  border-radius: 14px;
}

.venue-enquiry-rich-editor:focus-within {
  border-color: #fb7210;
  box-shadow: 0 0 0 3px rgba(251, 114, 16, 0.12);
}

.venue-enquiry-rich-editor--error {
  border-color: #f87171;
  background-color: #fff7f7;
}

.venue-enquiry-rich-editor :deep(.rich-text-editor__surface) {
  min-height: 108px;
  padding: 0.78rem 0.95rem;
  color: #1f2a44;
  font-size: 0.875rem;
  font-weight: 500;
}

:deep(.iti) {
  width: 100%;
}

:deep(.iti__tel-input) {
  width: 100%;
}

:deep(.iti .venue-enquiry-control) {
  padding-left: 5.4rem;
}

:global(.venue-enquiry-calendar.flatpickr-calendar) {
  overflow: hidden;
  border: 1px solid #eadfd4;
  border-radius: 18px;
  box-shadow: 0 24px 60px rgba(31, 18, 8, 0.18);
  font-family: inherit;
}

:global(.venue-enquiry-calendar .flatpickr-months) {
  background: #fff7ed;
}

:global(.venue-enquiry-calendar .flatpickr-month) {
  height: 46px;
  color: #1f1208;
}

:global(.venue-enquiry-calendar .flatpickr-current-month) {
  padding-top: 12px;
  font-size: 0.95rem;
  font-weight: 700;
}

:global(.venue-enquiry-calendar .flatpickr-prev-month),
:global(.venue-enquiry-calendar .flatpickr-next-month) {
  top: 8px;
  display: grid;
  height: 30px;
  width: 30px;
  place-items: center;
  border-radius: 999px;
  color: #7a4a24;
}

:global(.venue-enquiry-calendar span.flatpickr-weekday) {
  color: #9a7a63;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
}

:global(.venue-enquiry-calendar .flatpickr-day) {
  border-radius: 10px;
  color: #2f241b;
  font-weight: 600;
}

:global(.venue-enquiry-calendar .flatpickr-day.today) {
  border-color: #fb7210;
  color: #fb7210;
}

:global(.venue-enquiry-calendar .flatpickr-day.selected),
:global(.venue-enquiry-calendar .flatpickr-day.startRange),
:global(.venue-enquiry-calendar .flatpickr-day.endRange) {
  border-color: #fb7210;
  background: #fb7210;
  color: #fff;
}

:global(.venue-enquiry-calendar .flatpickr-day:hover) {
  border-color: #fff1e6;
  background: #fff1e6;
}
</style>
